import * as THREE from "three";
import type { LoadedModel } from "./types";
import { debugLog } from "./viewerUtils";

export function computeSceneBoundingBox(loadedModels: Map<string, LoadedModel>): THREE.Box3 {
  const box = new THREE.Box3();
  const tmp = new THREE.Box3();
  const matrix = new THREE.Matrix4();

  for (const entry of loadedModels.values()) {
    entry.model.object.updateMatrixWorld(true);

    entry.model.object.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh || !mesh.geometry) return;

      const pos = mesh.geometry.attributes.position;
      if (!pos || !(pos as THREE.BufferAttribute).array?.length) return;

      try {
        mesh.geometry.computeBoundingBox();
      } catch {
        return;
      }

      if (child instanceof THREE.InstancedMesh) {
        for (let i = 0; i < child.count; i++) {
          child.getMatrixAt(i, matrix);
          matrix.premultiply(child.matrixWorld);
          if (child.geometry.boundingBox) {
            tmp.copy(child.geometry.boundingBox).applyMatrix4(matrix);
            if (isFinite(tmp.min.x)) box.union(tmp);
          }
        }
      } else if (mesh.geometry.boundingBox) {
        tmp.copy(mesh.geometry.boundingBox).applyMatrix4(mesh.matrixWorld);
        if (isFinite(tmp.min.x)) box.union(tmp);
      }
    });
  }

  debugLog("[FragBimViewer] computed bounding box", {
    min: box.min.toArray().map((v) => +v.toFixed(2)),
    max: box.max.toArray().map((v) => +v.toFixed(2)),
    empty: box.isEmpty(),
  });

  return box;
}

export function fitCameraToScene(
  camera: THREE.PerspectiveCamera,
  controls: { target: THREE.Vector3; update: () => void },
  loadedModels: Map<string, LoadedModel>
) {
  const box = computeSceneBoundingBox(loadedModels);
  if (box.isEmpty()) {
    console.warn("[FragBimViewer] fit camera: bounding box is empty, skipping");
    return;
  }

  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const fovRad = (camera.fov * Math.PI) / 180;
  const distance = (maxDim / 2) / Math.tan(fovRad / 2) * 1.5;

  controls.target.copy(center);
  camera.position.set(center.x, center.y + distance * 0.4, center.z + distance);
  camera.lookAt(center);
  controls.update();

  debugLog("[FragBimViewer] fit camera to scene", {
    center: center.toArray().map((v) => +v.toFixed(2)),
    size: size.toArray().map((v) => +v.toFixed(2)),
    distance: +distance.toFixed(2),
  });
}
